<template>
  <section class="pt-8 px-8">
    <div>
      <!-- transaction details -->
      <div class="flex flex-col items-center py-2 justify-center gap-y-2">
        <h3 class="text-2xl font-semibold mt-1">
          {{
            transactionInfo.depositMethod === $transactionType.LOCAL_TRANSFER
              ? parseMoneyAll(
                  transactionInfo.localCurrencyDepositAmount.amount,
                  transactionInfo.localCurrencyDepositAmount.currency
                )
              : transactionInfo.depositMethod === $transactionType.WIRE_TRANSFER
              ? parseMoneyAll(
                  transactionInfo.fxCurrencyDepositAmount.amount,
                  transactionInfo.fxCurrencyDepositAmount.currency
                )
              : parseMoneyAll(
                  transactionInfo.depositAmount.amount,
                  transactionInfo.depositAmount.currency
                )
          }}
        </h3>
        <p
          class="text-sm font-normal text-fg-subtle text-center"
          style="word-break: normal"
          v-if="isLocalTransfer"
        >
          Please be aware that the exchange rate is dynamic and subject to
          change at any time while this transaction is being conducted.
        </p>
        <p
          class="text-sm font-normal text-center text-fg-subtle"
          style="word-break: normal"
          v-if="isWireTransfer"
        >
          Wire transfer transactions may take 2-4 business days before clearing
        </p>
        <p
          class="text-xs text-center font-normal rounded-lg px-1.5 py-2 capitalize mt-3 mb-2"
          :class="statusClass(transactionInfo.depositStatus)"
        >
          {{ formatServerName(transactionInfo.depositStatus) }}
        </p>
      </div>
    </div>
    <div class="text-fg-muted">
      <ul>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 lg:py-6"
        >
          <span>Time & Date</span>
          <span class="">{{ displayTimeDate }}</span>
        </li>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 lg:py-6"
        >
          <span>Transaction ID</span>
          <span
            >{{ maskID(transactionInfo.depositRequestID) }}
            <span
              class="cursor-pointer mb-1 ml-2"
              v-clipboard:copy="transactionInfo.depositRequestID"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
            >
              <copy-icon />
            </span>
          </span>
        </li>
        <li
          class="grid grid-cols-2 items-center text-sm font-normal border-b py-3.5 lg:py-6"
        >
          <span>Payment Method</span>
          <span class="flex items-center gap-x-2">
            <span class="capitalize">{{
              formatServerName(transactionInfo.depositMethod)
            }}</span>
          </span>
        </li>
        <li
          v-if="isAmountDeposited"
          class="grid grid-cols-2 items-center text-sm font-normal border-b py-3.5 lg:py-6"
        >
          <span>Deposited Amount</span>
          <span class="capitalize">
            {{
              parseMoneyAll(
                transactionDetails.depositedAmount,
                transactionDetails.depositedCurrency
              )
            }}
          </span>
        </li>
        <li
          class="grid grid-cols-2 items-center text-sm font-normal border-b py-3.5 lg:py-6"
        >
          <span>Fees</span>
          <span class="flex items-center gap-x-2">
            <span class="capitalize">{{
              parseMoneyAll(0, transactionInfo.depositedCurrency)
            }}</span>
          </span>
        </li>
        <li
          class="grid grid-cols-2 items-center text-sm font-normal border-b py-3.5 lg:py-6"
        >
          <span>Deposit Reference</span>
          <span class="flex items-center gap-x-2">
            <span>
              {{ maskID(transactionInfo.depositReference) }}
              <span
                class="cursor-pointer mb-1 ml-2"
                v-clipboard:copy="transactionInfo.depositReference"
                v-clipboard:success="onCopy"
                v-clipboard:error="onError"
              >
                <copy-icon />
              </span>
            </span>
          </span>
        </li>
        <li
          v-if="
            transactionInfo.transactionType !== 'deposit' && !isCurrencySwap
          "
          class="grid grid-cols-2 items-center text-sm font-normal border-b py-3.5 lg:py-6"
        >
          <span>Rate</span>
          <span class="flex items-center gap-x-2">
            <span class="capitalize">
              {{
                transactionInfo.transferInstruction.division_operator
                  ? transactionInfo.transferInstruction.from
                  : transactionInfo.transferInstruction.to
              }}
              {{ transactionInfo.transferInstruction.exchange_rate || '--' }}
            </span>
          </span>
        </li>
        <li
          v-if="
            transactionInfo.transactionType === 'deposit' &&
            transactionInfo.depositStatus === $STATUS.REFUNDED
          "
          class="grid grid-cols-2 items-center text-sm font-normal border-b py-3.5 lg:py-6"
        >
          <span>Refund Reason</span>
          <span class="flex items-center gap-x-2">
            <span class="capitalize">
              {{
                transactionInfo.reversalDetails &&
                transactionInfo.reversalDetails.notes
                  ? transactionInfo.reversalDetails.notes
                  : '--'
              }}
            </span>
          </span>
        </li>
        <li
          v-if="isLocalTransfer || isWireTransfer"
          class="flex justify-between font-normal text-sm items-center py-3.5 lg:py-5"
        >
          <span>Deposit Details</span>
          <span
            class="text-primary font-semibold text-2xl cursor-pointer"
            @click="toggleShow('showDepositDetails')"
            >{{ showDepositDetails ? '-' : '+' }}</span
          >
        </li>
        <transition name="slide-fade">
          <div v-if="showDepositDetails" style="word-break: normal">
            <div class="pt-3" v-if="isLocalTransfer" style="word-break: normal">
              <div
                v-for="(detail, index) in localTransferDetails"
                :key="index"
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">{{ detail.label }}</p>
                <div class="flex items-center gap-x-3">
                  <p class="text-sm font-semibold">
                    {{ detail.value || '--' }}
                  </p>
                  <div
                    class="cursor-pointer"
                    v-clipboard:copy="detail.value"
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </div>
                </div>
              </div>
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Reference</p>
                <div class="flex items-center gap-x-3">
                  <p class="text-sm font-bold">
                    {{ maskID(transactionInfo.depositReference) || '--' }}
                  </p>
                  <div
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="transactionInfo.depositReference"
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </div>
                </div>
              </div>
              <div
                class="w-full text-xs mt-4 md:text-base font-semibold hover:opacity-80 bg-root border-primary rounded-full border flex justify-center py-2 cursor-pointer text-primary"
                @click="generateLocalPdf()"
              >
                Download
              </div>
            </div>
            <div
              class="pt-3"
              v-if="isWireTransferInformation"
              style="word-break: normal"
            >
              <div v-for="(detail, index) in wireTransferDetails" :key="index">
                <div
                  v-if="valueExists(detail.value)"
                  class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
                >
                  <p class="text-sm font-thin">{{ detail.key }}</p>
                  <div class="flex items-center gap-x-3">
                    <p class="text-xs font-semibold">
                      {{ detail.value || '--' }}
                    </p>
                    <div
                      class="cursor-pointer mb-1 ml-2"
                      v-clipboard:copy="detail.value"
                      v-clipboard:success="onCopy"
                      v-clipboard:error="onError"
                    >
                      <copy-icon />
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="w-full text-xs my-2 md:text-base font-semibold hover:opacity-80 bg-root border-primary rounded-full border flex justify-center py-2 cursor-pointer text-primary"
                @click="generatePdf()"
              >
                Download
              </div>
            </div>
            <div v-if="isGBPorEURTransaction" class="mt-3">
              <div
                v-for="(item, index) in bankDetails"
                :key="index"
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">{{ item.key }}</p>
                <div class="flex items-center gap-x-3">
                  <p class="text-xs font-semibold">{{ item.value || '--' }}</p>
                  <div
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="item.value"
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </ul>
    </div>

    <vuehtml2pdf
      :manual-pagination="true"
      :filename="fileName"
      :pdf-quality="2"
      pdf-format="a4"
      pdf-content-width="800px"
      :enable-download="true"
      ref="DownloadComp"
      :show-layout="false"
    >
      <section slot="pdf-content">
        <deposit-details />
      </section>
    </vuehtml2pdf>

    <vuehtml2pdf
      :manual-pagination="true"
      :filename="fileName"
      :pdf-quality="2"
      pdf-format="a4"
      pdf-content-width="800px"
      :enable-download="true"
      ref="DownloadLocalComp"
      :show-layout="false"
    >
      <section slot="pdf-content">
        <local-deposit-details />
      </section>
    </vuehtml2pdf>
  </section>
</template>

<script>
import {
  AddComma,
  ParseMoneyAll,
  ParseMoney,
  GetCurrencyCountry,
  MaskWalletAddressTwo,
  ConvertValueToXDP,
  StatusClass,
  FormatServerName
} from '@/utils/helpers.js'
import tooltipDateContent from '@/mixins/app.js'

export default {
  mixins: [tooltipDateContent],
  props: {
    transactionDetails: {
      type: Object,
      default: () => {}
    },
    rateIncreased: {
      type: Boolean,
      default: () => false,
      required: true
    }
  },
  data () {
    return {
      showDepositDetails: false
    }
  },
  methods: {
    statusClass (status) {
      return StatusClass(status)
    },
    formatServerName (val) {
      return FormatServerName(val)
    },
    handleClose () {
      this.$emit('closeDrawer')
    },
    convertValueTo2DP (val) {
      return ConvertValueToXDP(val, 2)
    },
    toggleShow (arg) {
      this[arg] = !this[arg]
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    addComma (val) {
      return AddComma(val.toString())
    },
    maskID (val) {
      return MaskWalletAddressTwo(val)
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Copied'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    showDepositDetailsHandler () {
      this.showDepositDetails = !this.showDepositDetails
    },
    parseMoney (amount) {
      return ParseMoney(amount)
    },
    valueExists (val) {
      return val && val.length > 0
    },
    generatePdf () {
      this.$refs.DownloadComp.generatePdf()
    },
    generateLocalPdf () {
      this.$refs.DownloadLocalComp.generatePdf()
    }
  },
  components: {
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    Transition: () =>
      import('@/components/v2/animation/transition/Transition.vue'),
    DepositDetails: () => import('@/components/deposit/DepositDetails.vue'),
    LocalDepositDetails: () =>
      import('@/components/deposit/LocalDepositDetails.vue')
  },
  computed: {
    isWireTransferInformation () {
      return this.isWireTransfer && !this.isGBPorEURTransaction
    },
    isGBPorEURTransaction () {
      return (
        this.transactionInfo.depositedCurrency === this.$CURRENCY.GBP ||
        this.transactionInfo.depositedCurrency === this.$CURRENCY.EUR
      )
    },
    wireTransferDetails () {
      return [
        {
          key: 'Bank Name',
          value:
            this.transactionInfo?.transferInstruction?.wireInstruction
              ?.depositoryBankName
        },
        {
          key: 'Account Number',
          value:
            this.transactionInfo?.transferInstruction?.wireInstruction
              ?.accountNumber
        },
        {
          key: 'Bank Phone',
          value:
            this.transactionInfo?.transferInstruction?.wireInstruction
              ?.bankPhone
        },
        {
          key: 'Credit To',
          value:
            this.transactionInfo?.transferInstruction?.wireInstruction?.creditTo
        },
        {
          key: 'Beneficiary Address',
          value:
            this.transactionInfo?.transferInstruction?.wireInstruction
              ?.beneficiaryAddress
        },
        {
          key: 'Bank Address',
          value:
            this.transactionInfo?.transferInstruction?.wireInstruction
              ?.bankAddress
        },
        {
          key: 'Reference',
          value: this.maskID(
            this.transactionInfo?.transferInstruction?.wireInstruction
              ?.reference
          )
        },
        {
          key: 'Routing Number',
          value:
            this.transactionInfo?.transferInstruction?.wireInstruction
              ?.routingNumber
        }
      ]
    },
    bankDetails () {
      return [
        {
          key: 'Account Number',
          value:
            this.transactionInfo.transferInstruction.wireInstruction
              ?.accountNumber
        },
        {
          key: 'Bank Name',
          value:
            this.transactionInfo.transferInstruction.wireInstruction?.creditTo
        },
        {
          key: 'IBAN',
          value: this.transactionInfo.transferInstruction.wireInstruction?.iban
        },
        {
          key: 'Swift Code',
          value:
            this.transactionInfo.transferInstruction.wireInstruction?.swiftCode
        }
      ]
    },
    isWireTransfer () {
      return (
        this.transactionInfo.depositMethod ===
        this.$transactionType.WIRE_TRANSFER
      )
    },
    isLocalTransfer () {
      return (
        this.transactionInfo.depositMethod ===
        this.$transactionType.LOCAL_TRANSFER
      )
    },
    localTransferDetails () {
      return [
        {
          label: 'Bank Name',
          value:
            this.transactionInfo?.transferInstruction?.account_details?.bankName
        },
        {
          label: 'Account Number',
          value:
            this.transactionInfo?.transferInstruction?.account_details
              ?.accountNumber
        },
        {
          label: 'Account Name',
          value:
            this.transactionInfo?.transferInstruction?.account_details
              ?.accountName
        }
      ]
    },
    isCurrencySwap () {
      return (
        this.transactionInfo.transactionType ===
        this.$transactionType.CURRENCY_SWAP
      )
    },
    isAmountDeposited () {
      return (
        this.transactionDetails.type === this.$transactionType.DEPOSIT &&
        this.isCurrencySwap
      )
    },
    transactionInfo () {
      return this.$store.state.transactions.transactionDetails
    },
    displayTimeDate () {
      const timeToPick =
        this.transactionInfo.depositStatus === this.$STATUS.COMPLETED
          ? this.transactionInfo.depositRequestCompletedAt
          : this.transactionInfo.depositRequestCreatedAt
      return this.tooltipDateContent(timeToPick)
    },
    fileName () {
      return `${process.env.VUE_APP_TENANT_TITLE} Deposit Details`
    }
  }
}
</script>
